import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  LogOut,
  Mail,
  Phone,
  RefreshCw,
  Lock,
  Eye,
  EyeOff,
  Inbox,
  Reply,
  ArrowLeft,
} from "lucide-react";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const AdminPage = () => {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [enquiries, setEnquiries] = useState([]);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const loadEnquiries = async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch (e) {
      if (e.response?.status === 401) setUser(false);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (user) loadEnquiries();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      setUser(data);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(false);
      setEnquiries([]);
      toast.success("Logged out");
    }
  };

  const toggleRead = async (enq) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/enquiries/${enq.id}/read`,
        { read: !enq.read },
        { withCredentials: true }
      );
      setEnquiries((list) => list.map((x) => (x.id === data.id ? data : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unread = enquiries.filter((x) => !x.read).length;
  const inputCls =
    "w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-3.5 text-sm text-cream placeholder:text-cream/35 outline-none focus:border-sunset transition-colors duration-300";

  if (user === null) {
    return (
      <div className="min-h-screen bg-forest flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.3em] text-gold uppercase">Checking session...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-forest grain flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <button
            data-testid="admin-back-home"
            onClick={() => (window.location.href = "/")}
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50 hover:text-gold transition-colors duration-300"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to website
          </button>

          <div className="mt-6 rounded-2xl border border-gold/25 bg-forest-surface/60 backdrop-blur-xl p-8">
            <span className="w-12 h-12 rounded-full bg-sunset/15 text-sunset flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </span>
            <h1 className="mt-5 font-display text-3xl text-cream">Admin Inbox</h1>
            <p className="mt-2 text-sm text-cream/55 leading-relaxed">
              Sign in with the admin account to read website enquiries.
            </p>

            <form data-testid="admin-login-form" onSubmit={login} className="mt-7 space-y-5">
              <div>
                <label htmlFor="ad-email" className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50">
                  Email
                </label>
                <input
                  id="ad-email"
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={creds.email}
                  onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
                  placeholder="admin@neshaglobal.com"
                  className={`${inputCls} mt-2`}
                />
              </div>
              <div>
                <label htmlFor="ad-password" className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50">
                  Password
                </label>
                <div className="relative mt-2">
                  <input
                    id="ad-password"
                    data-testid="admin-password-input"
                    type={showPw ? "text" : "password"}
                    required
                    value={creds.password}
                    onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                    placeholder="••••••••"
                    className={`${inputCls} pr-12`}
                  />
                  <button
                    type="button"
                    data-testid="admin-toggle-password"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/40 hover:text-gold transition-colors duration-300"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button
                data-testid="admin-login-button"
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-sunset py-3.5 font-grotesk text-sm font-semibold uppercase tracking-wider text-forest hover:bg-sunset-bright transition-colors duration-300 disabled:opacity-60"
              >
                {busy ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream font-body text-charcoal">
      <header className="bg-forest border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full bg-white ring-1 ring-gold/50 overflow-hidden">
              <img src="/assets/nesha-logo.png" alt="Nesha Global logo" className="w-full h-full object-cover" />
            </span>
            <div>
              <p className="font-display text-xl tracking-[0.15em] text-cream uppercase">Admin Inbox</p>
              <p className="font-mono text-[9px] tracking-[0.3em] text-gold uppercase">
                {user.email} {unread > 0 ? `— ${unread} unread` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-button"
              onClick={loadEnquiries}
              className="flex items-center gap-2 rounded-full border border-cream/25 px-4 py-2.5 font-grotesk text-xs uppercase tracking-wider text-cream/80 hover:text-gold hover:border-gold/50 transition-colors duration-300"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingList ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-sunset px-4 py-2.5 font-grotesk text-xs font-semibold uppercase tracking-wider text-forest hover:bg-sunset-bright transition-colors duration-300"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        {enquiries.length === 0 && !loadingList && (
          <div className="rounded-2xl border border-forest/15 bg-cream-card p-12 text-center">
            <Inbox className="w-8 h-8 mx-auto text-forest/40" />
            <p className="mt-4 font-grotesk font-semibold text-forest">No enquiries yet</p>
            <p className="mt-1 text-sm text-mutedtext">
              New enquiries from the website contact form will appear here.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {enquiries.map((enq, i) => (
            <motion.article
              key={enq.id}
              data-testid={`admin-enquiry-item-${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
              className={`rounded-2xl border p-6 transition-colors duration-300 ${
                enq.read ? "bg-cream-card border-forest/12" : "bg-white border-sunset shadow-md"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    {!enq.read && <span data-testid={`admin-unread-dot-${i}`} className="w-2 h-2 rounded-full bg-sunset" />}
                    <h3 className="font-grotesk font-semibold text-lg text-forest">{enq.name}</h3>
                    <span className="rounded-full bg-forest/5 border border-forest/15 px-3 py-0.5 font-mono text-[10px] uppercase tracking-wider text-mud">
                      {enq.product || "General"}
                    </span>
                  </div>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-mutedtext uppercase">
                    {fmtDate(enq.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    data-testid={`admin-reply-${i}`}
                    href={`mailto:${enq.email}?subject=${encodeURIComponent(`Re: your enquiry — Nesha Global Pvt Ltd`)}`}
                    className="flex items-center gap-2 rounded-full bg-forest px-4 py-2 font-grotesk text-xs font-semibold uppercase tracking-wider text-cream hover:bg-forest-surface transition-colors duration-300"
                  >
                    <Reply className="w-3.5 h-3.5" />
                    Reply by Email
                  </a>
                  <button
                    data-testid={`admin-toggle-read-${i}`}
                    onClick={() => toggleRead(enq)}
                    className="flex items-center gap-2 rounded-full border border-forest/20 px-4 py-2 font-grotesk text-xs uppercase tracking-wider text-forest/70 hover:border-gold hover:text-gold transition-colors duration-300"
                  >
                    {enq.read ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {enq.read ? "Mark Unread" : "Mark Read"}
                  </button>
                </div>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-charcoal/85 whitespace-pre-line">{enq.message}</p>

              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-forest/10 pt-4 font-mono text-xs text-mutedtext">
                <a
                  data-testid={`admin-enquiry-email-${i}`}
                  href={`mailto:${enq.email}`}
                  className="flex items-center gap-2 hover:text-gold transition-colors duration-300"
                >
                  <Mail className="w-3.5 h-3.5" /> {enq.email}
                </a>
                {enq.phone && (
                  <a
                    data-testid={`admin-enquiry-phone-${i}`}
                    href={`tel:${enq.phone}`}
                    className="flex items-center gap-2 hover:text-gold transition-colors duration-300"
                  >
                    <Phone className="w-3.5 h-3.5" /> {enq.phone}
                  </a>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
